import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from 'lane-shared/contexts';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { imageUrl } from 'lane-shared/helpers/formatters';
import { Button, Icon } from 'design-system-web';
import Image from 'components/general/Image';
import { Typography } from 'components/lds';
import styles from './IOSSmartBanner.scss';
import { IOS_BANNER_DISMISSED } from 'helpers/constants/localstorageKey';
import * as Bowser from 'bowser';
import isIOSDevice from 'helpers/isIOSDevice';

// Helper function to check if the banner has been dismissed
function getBannerDismissalStatus(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return localStorage.getItem(IOS_BANNER_DISMISSED) === 'true';
  } catch (error) {
    // Handle localStorage not available or other errors
    console.warn('Could not save banner dismissal status:', error);

    return false;
  }
}

// Helper function to set banner dismissal status
function setBannerDismissalStatus(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(IOS_BANNER_DISMISSED, 'true');
  } catch (error) {
    // Handle localStorage not available or other errors
    console.warn('Could not save banner dismissal status:', error);
  }
}

// Helper function to detect Safari browser
function isSafariBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;

  const parser = Bowser.getParser(navigator.userAgent);

  return Boolean(parser.satisfies({
    ios: { safari: '>=11' },
  }));
}

export default function IOSSmartBanner() {
  const { t } = useTranslation();
  const [showIOSBanner, setShowIOSBanner] = useState<boolean>(false);
  const { whitelabel } = useContext(AppContext);

  async function handleAppAction() {
    // Direct link to App Store with null check
    if (whitelabel?.mobileIos) {
      window.open(whitelabel.mobileIos, '_blank');
      dismissBanner();
    }
  }

  function dismissBanner() {
    setShowIOSBanner(false);
    setBannerDismissalStatus();
  }

  useEffect(() => {
    if (
      !whitelabel?.mobileIos ||
      !whitelabel?.profile?.logo ||
      !whitelabel?.profile?.name ||
      !isIOSDevice() ||
      isSafariBrowser() ||
      getBannerDismissalStatus()
    ) {
      setShowIOSBanner(false);

      return;
    }

    setShowIOSBanner(true);
  }, [whitelabel?.mobileIos, whitelabel?.profile?.logo, whitelabel?.profile?.name]);

  // Don't render if profile data is missing
  if (!whitelabel?.profile?.logo || !whitelabel?.profile?.name) {
    return null;
  }

  return showIOSBanner ? (
    <div className={styles.banner}>
      <Icon
        className={styles.icon}
        onClick={dismissBanner}
        name="times"
        set={ICON_SET_FONTAWESOME}
      />
      <Image
        className={styles.logo}
        src={imageUrl(whitelabel.profile.logo)}
        alt={`${whitelabel.profile.name} logo`}
      />
      <div>
        <Typography type="s" mode="primary" className={styles.appText}>
          {whitelabel.profile.name}
        </Typography>
        <Typography type="xs" mode="secondary">
          {t('Download the app on the Apple Store')}
        </Typography>
      </div>
      <Button 
        className={styles.viewButton} 
        type="button"
        onClick={handleAppAction}
      >
        {t('View')}
      </Button>
    </div>
  ) : null;
}
